import { useState, useEffect } from 'react'
import { ExternalLink, Calendar, DollarSign, Clock, Filter } from 'lucide-react'
import { Journal, WorkType } from '@/types'
import { format, isAfter, isBefore } from 'date-fns'

const workTypeLabels: Record<WorkType, string> = {
  [WorkType.POEM]: 'Poetry',
  [WorkType.SHORT_STORY]: 'Short Story',
  [WorkType.ESSAY]: 'Essay',
  [WorkType.NOVEL]: 'Novel',
  [WorkType.FLASH_FICTION]: 'Flash Fiction'
}

export default function Journals() {
  const [journals, setJournals] = useState<Journal[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<{
    type: WorkType | 'all'
    status: 'all' | 'open' | 'closed'
    search: string
  }>({
    type: 'all',
    status: 'all',
    search: ''
  })

  useEffect(() => {
    // TODO: Fetch journals from API
    const mockJournals: Journal[] = [
      {
        id: '1',
        name: 'Poetry Magazine',
        description: 'A prestigious monthly publication featuring contemporary poetry',
        website: 'https://poetrymagazine.com',
        submissionEmail: 'submissions@poetrymagazine.com',
        acceptedTypes: [WorkType.POEM],
        responseTime: 90,
        paymentRate: '$50-200 per poem',
        isActive: true,
        createdAt: '2024-01-01T10:00:00Z',
        updatedAt: '2024-01-01T10:00:00Z',
        readingPeriods: [
          {
            id: '1',
            journalId: '1',
            startDate: '2024-01-01',
            endDate: '2024-03-31',
            isOpen: true,
            specialNotes: 'Spring submissions'
          }
        ]
      },
      {
        id: '2',
        name: 'Literary Quarterly',
        description: 'Quarterly journal of fiction, poetry, and essays',
        website: 'https://literaryquarterly.com',
        submissionEmail: 'submit@literaryquarterly.com',
        acceptedTypes: [WorkType.POEM, WorkType.SHORT_STORY, WorkType.ESSAY],
        responseTime: 120,
        paymentRate: '$100-500 per piece',
        isActive: true,
        createdAt: '2024-01-01T10:00:00Z',
        updatedAt: '2024-01-01T10:00:00Z',
        readingPeriods: [
          {
            id: '2',
            journalId: '2',
            startDate: '2024-04-01',
            endDate: '2024-04-30',
            isOpen: false,
            specialNotes: 'Summer issue deadline'
          }
        ]
      }
    ]
    setJournals(mockJournals)
    setLoading(false)
  }, [])

  const getReadingPeriodStatus = (journal: Journal) => {
    if (!journal.readingPeriods || journal.readingPeriods.length === 0) {
      return { status: 'unknown', period: null, daysUntil: null }
    }

    const now = new Date()
    const openPeriod = journal.readingPeriods.find(period => {
      const start = new Date(period.startDate)
      const end = new Date(period.endDate)
      return !isBefore(now, start) && !isAfter(now, end)
    })

    if (openPeriod) {
      const daysUntil = Math.ceil((new Date(openPeriod.endDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      return { status: 'open', period: openPeriod, daysUntil }
    }

    // Find next opening period
    const futurePeriod = journal.readingPeriods
      .filter(period => isAfter(new Date(period.startDate), now))
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())[0]

    if (futurePeriod) {
      const daysUntil = Math.ceil((new Date(futurePeriod.startDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      return { status: 'closed', period: futurePeriod, daysUntil }
    }

    return { status: 'closed', period: null, daysUntil: null }
  }

  const filteredJournals = journals.filter(journal => {
    if (filter.search && !journal.name.toLowerCase().includes(filter.search.toLowerCase())) {
      return false
    }

    if (filter.type !== 'all' && !journal.acceptedTypes.includes(filter.type)) {
      return false
    }

    if (filter.status !== 'all') {
      const { status } = getReadingPeriodStatus(journal)
      if (filter.status === 'open' && status !== 'open') return false
      if (filter.status === 'closed' && status === 'open') return false
    }

    return true
  })

  if (loading) {
    return <div className="text-center py-8">Loading journals...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Literary Journals</h1>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <Filter size={20} className="text-gray-500" />
          
          <input
            type="text"
            placeholder="Search journals..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            className="input max-w-xs"
          />

          <select
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value as WorkType | 'all' })}
            className="input max-w-xs"
          >
            <option value="all">All Types</option>
            {Object.entries(workTypeLabels).map(([type, label]) => (
              <option key={type} value={type}>{label}</option>
            ))}
          </select>

          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value as 'all' | 'open' | 'closed' })}
            className="input max-w-xs"
          >
            <option value="all">All Status</option>
            <option value="open">Open for Submissions</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Journals List */}
      {filteredJournals.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">No journals found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your filters to see more results.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredJournals.map((journal) => {
            const { status, period, daysUntil } = getReadingPeriodStatus(journal)
            
            return (
              <div key={journal.id} className="card">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {journal.name}
                    </h3>
                    {status === 'open' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Open ({daysUntil} days left)
                      </span>
                    ) : status === 'closed' && period && daysUntil ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Closed (opens in {daysUntil} days)
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Closed
                      </span>
                    )}
                  </div>
                  
                  {journal.website && (
                    <a
                      href={journal.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <ExternalLink size={18} />
                    </a>
                  )}
                </div>

                {journal.description && (
                  <p className="text-gray-600 mb-4 text-sm">
                    {journal.description}
                  </p>
                )}

                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium mr-2">Accepts:</span>
                    {journal.acceptedTypes.map(type => workTypeLabels[type]).join(', ')}
                  </div>

                  {journal.responseTime && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock size={16} className="mr-2" />
                      <span>Response time: {journal.responseTime} days</span>
                    </div>
                  )}

                  {journal.paymentRate && (
                    <div className="flex items-center text-sm text-gray-600">
                      <DollarSign size={16} className="mr-2" />
                      <span>Payment: {journal.paymentRate}</span>
                    </div>
                  )}

                  {period && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar size={16} className="mr-2" />
                      <span>
                        {status === 'open' ? 'Current period' : 'Next period'}: {' '}
                        {format(new Date(period.startDate), 'MMM d')} - {format(new Date(period.endDate), 'MMM d, yyyy')}
                      </span>
                    </div>
                  )}

                  {period?.specialNotes && (
                    <p className="text-sm text-gray-600 italic">
                      {period.specialNotes}
                    </p>
                  )}
                </div>

                <div className="flex justify-between items-center">
                  <div>
                    {journal.submissionEmail && (
                      <a
                        href={`mailto:${journal.submissionEmail}`}
                        className="text-primary-600 hover:text-primary-900 text-sm"
                      >
                        {journal.submissionEmail}
                      </a>
                    )}
                  </div>
                  
                  {status === 'open' && (
                    <button className="btn btn-primary btn-sm">
                      Submit Work
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}